import { defineStore } from 'pinia';
import { collection, collectionGroup, deleteField, doc, getDoc, getDocs, increment, query, setDoc, updateDoc, where } from 'firebase/firestore';
import type { Tenant } from '~/types/models';
import { stripUndefined } from '~/lib/stripUndefined';
import { getTenantSubscription, resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { formatStorageSize, normalizeStorageBytes, toStorageLimitBytes } from '~/lib/storageUsage';
import { IDENTITY_DATA_MAX_AGE_MS, runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';

type DomainVerificationResult = {
    verified: boolean,
    txtVerified: boolean,
    dnsTargetVerified: boolean,
    expectedTarget: string | null,
    routingRecordType: 'CNAME' | 'A' | 'AAAA' | null,
    vercelIntegrationEnabled: boolean,
    vercelProvisionStatus: 'skipped' | 'created' | 'already_exists' | 'failed',
    vercelError: string | null,
    vercelVerified: boolean,
    vercelVerification: Array<{ type: string; domain: string; value: string; reason?: string }>,
    vercelRecommendedTarget: string | null,
}

export const useTenantStore = defineStore('tenant', {
    state: () => ({
        tenants: [] as Array<{ tenantId: string; role: string }>,
        currentTenant: null as Tenant | null,
        currentTenantId: null as string | null,
        loadedForUserId: null as string | null,
        loadedTenantDocumentId: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    actions: {
        /**
         * Fallback resolver for users whose `users/{uid}.tenants` array is missing
         * or stale. It scans active member entries by email and reconstructs the
         * tenant membership list from `tenants/{tenantId}/members/*` documents.
         */
        async recoverTenantsFromMembership(userId: string, email: string): Promise<Array<{ tenantId: string; role: string }>> {
            const { $db } = useNuxtApp();
            const normalizedUserId = userId.trim();
            const normalizedEmail = email.trim().toLowerCase();
            if (!normalizedUserId && !normalizedEmail) return [];

            const collectTenantRoles = (docs: Array<any>) => docs
                .map((memberDoc) => {
                    const segments = memberDoc.ref.path.split('/');
                    const tenantId = segments.length >= 2 ? segments[1] : '';
                    const role = String(memberDoc.data()?.role || 'member');
                    return tenantId ? { tenantId, role } : null;
                })
                .filter((item): item is { tenantId: string; role: string } => !!item);

            try {
                const membersRef = collectionGroup($db, 'members');
                const recovered: Array<{ tenantId: string; role: string }> = [];

                if (normalizedUserId) {
                    const byUserIdQuery = query(
                        membersRef,
                        where('userId', '==', normalizedUserId),
                        where('status', '==', 'active'),
                    );
                    const byUserIdSnap = await getDocs(byUserIdQuery);
                    recovered.push(...collectTenantRoles(byUserIdSnap.docs));
                }

                if (recovered.length === 0 && normalizedEmail) {
                    const byEmailQuery = query(
                        membersRef,
                        where('email', '==', normalizedEmail),
                        where('status', '==', 'active'),
                    );
                    const byEmailSnap = await getDocs(byEmailQuery);
                    recovered.push(...collectTenantRoles(byEmailSnap.docs));
                }

                const deduped = Array.from(new Map(recovered.map(item => [item.tenantId, item])).values());
                return deduped;
            } catch (error) {
                const message = String(error || 'Unknown error');
                if (!message.includes('permission-denied')) {
                    console.error('Error recovering tenant memberships:', error);
                }
                return [];
            }
        },

        /**
         * Fetches user's tenants from their user document
         */
        async fetchUserTenants(userId: string, options: FreshRequestOptions = {}): Promise<boolean> {
            const normalizedUserId = String(userId || '').trim();
            const result = await runFreshRequest(
                `tenant-memberships:${normalizedUserId}`,
                () => this._fetchUserTenantsFromFirestore(normalizedUserId),
                {
                    maxAgeMs: IDENTITY_DATA_MAX_AGE_MS,
                    ...options,
                    hasData: this.loadedForUserId === normalizedUserId,
                },
            );
            return result ?? true;
        },

        async _fetchUserTenantsFromFirestore(userId: string): Promise<boolean> {
            const { $db } = useNuxtApp();
            const auth = useNuxtApp().$auth;
            this.loading = true;
            this.error = null;
            const normalizedUserId = String(userId || '').trim();

            if (this.loadedForUserId !== normalizedUserId) {
                this.clearTenants();
            }

            try {
                const userDocRef = doc($db, 'users', userId);
                const userSnap = await getDoc(userDocRef);

                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    this.tenants = userData.tenants || [];

                    // Self-heal: invited members can have a valid `members/*` entry
                    // without a populated users/{uid}.tenants array.
                    if (this.tenants.length === 0) {
                        const recoveredTenants = await this.recoverTenantsFromMembership(
                            auth.currentUser?.uid || '',
                            auth.currentUser?.email || '',
                        );
                        if (recoveredTenants.length > 0) {
                            this.tenants = recoveredTenants;

                            // Best-effort persistence so subsequent logins use the fast path.
                            try {
                                await updateDoc(userDocRef, {
                                    tenants: recoveredTenants,
                                    updatedAt: new Date(),
                                });
                            } catch (persistError) {
                                console.warn('Unable to persist recovered tenant memberships:', persistError);
                            }
                        }
                    }

                    // Fetch the first tenant's full data
                    if (this.tenants.length > 0) {
                        const firstTenant = this.tenants[0];
                        if (firstTenant) {
                            await this.setCurrentTenant(firstTenant.tenantId);
                        }
                    }
                } else {
                    // Bootstrap path: account exists in Auth but user profile doc
                    // is missing. Recover tenant memberships from member records
                    // and create the user doc so future logins use normal flow.
                    const recoveredTenants = await this.recoverTenantsFromMembership(
                        auth.currentUser?.uid || '',
                        auth.currentUser?.email || '',
                    );

                    if (recoveredTenants.length > 0) {
                        this.tenants = recoveredTenants;

                        try {
                            await setDoc(userDocRef, {
                                email: String(auth.currentUser?.email || '').toLowerCase(),
                                displayName: auth.currentUser?.displayName || '',
                                fullName: auth.currentUser?.displayName || '',
                                avatarURL: auth.currentUser?.photoURL || '',
                                tenants: recoveredTenants,
                                createdAt: new Date(),
                                updatedAt: new Date(),
                            }, { merge: true });
                        } catch (persistError) {
                            console.warn('Unable to create missing user document during tenant recovery:', persistError);
                        }

                        const firstTenant = this.tenants[0];
                        if (firstTenant) {
                            await this.setCurrentTenant(firstTenant.tenantId);
                        }
                    } else {
                        this.error = 'User not found';
                    }
                }
                this.loadedForUserId = normalizedUserId;
                return true;
            } catch (error) {
                this.error = `Error fetching user tenants: ${error}`;
                console.error(this.error);
                this.loadedForUserId = null;
                return false;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Fetches a specific tenant's full data from Firestore
         */
        async fetchTenant(tenantId: string, options: FreshRequestOptions = {}): Promise<void> {
            await runFreshRequest(
                `tenant-document:${tenantId}`,
                () => this._fetchTenantFromFirestore(tenantId),
                {
                    maxAgeMs: IDENTITY_DATA_MAX_AGE_MS,
                    ...options,
                    hasData: this.loadedTenantDocumentId === tenantId && this.currentTenantId === tenantId,
                },
            );
        },

        async _fetchTenantFromFirestore(tenantId: string): Promise<void> {
            const { $db } = useNuxtApp();

            // Keep tenant context available even if reading the tenant document
            // itself is blocked by rules. Most app data reads only need tenantId.
            this.currentTenantId = tenantId;

            try {
                const tenantDocRef = doc($db, 'tenants', tenantId);
                const tenantSnap = await getDoc(tenantDocRef);

                if (tenantSnap.exists()) {
                    this.currentTenant = { id: tenantSnap.id, ...tenantSnap.data() } as Tenant;
                    this.loadedTenantDocumentId = tenantId;
                } else {
                    this.error = 'Tenant not found';
                }
            } catch (error) {
                const message = String((error as { code?: string; message?: string } | null)?.code || (error as { message?: string } | null)?.message || error || 'Unknown error');
                if (message.includes('permission-denied') || message.includes('insufficient permissions')) {
                    // Non-fatal for client portal sessions: hydrate safe public
                    // branding/currency data instead of requiring tenant membership.
                    this.error = null;
                    try {
                        const response = await $fetch<{ tenant: Partial<Tenant> | null }>('/api/public/tenant-branding', {
                            query: { tenantId },
                        });
                        if (response.tenant) {
                            this.currentTenant = {
                                id: tenantId,
                                name: response.tenant.name || 'Workspace',
                                industry: '',
                                tenantImageURL: response.tenant.tenantImageURL || '',
                                portalBranding: response.tenant.portalBranding,
                                paymentSettings: response.tenant.paymentSettings,
                                projectSettings: response.tenant.projectSettings,
                                customDomain: response.tenant.customDomain,
                                domainVerified: response.tenant.domainVerified,
                                authSettings: response.tenant.authSettings,
                                createdAt: new Date(),
                                updatedAt: new Date(),
                            } as Tenant;
                            this.loadedTenantDocumentId = tenantId;
                        }
                    } catch (brandingError) {
                        console.warn('Tenant document read denied and public branding fallback failed; continuing with tenantId-only context.', brandingError);
                    }
                } else {
                    this.error = `Error fetching tenant: ${error}`;
                    console.error(this.error);
                }
            }
        },

        /**
         * Sets the current active tenant and fetches its data
         */
        async setCurrentTenant(tenantId: string): Promise<void> {
            const tenant = this.tenants.find(t => t.tenantId === tenantId);
            if (tenant) {
                this.currentTenantId = tenantId;
                await this.fetchTenant(tenantId);
            } else {
                this.error = 'Tenant not found in user tenants';
            }
        },

        /**
         * Clears tenant data (on logout)
         */
        clearTenants(): void {
            this.tenants = [];
            this.currentTenant = null;
            this.currentTenantId = null;
            this.loadedForUserId = null;
            this.loadedTenantDocumentId = null;
        },

        /**
         * Resolves a tenant by its verified custom domain.
         * Used when the app is accessed via a branded hostname rather than
         * the default slug-based URL.
         *
         * Requires a Firestore composite index on (customDomain ASC, domainVerified ASC)
         * in the `tenants` collection to avoid a full collection scan in production.
         *
         * @param hostname - The hostname to look up (e.g. "app.mybrand.com").
         * @returns The tenant ID if a verified match is found, otherwise null.
         */
        async resolveTenantByDomain(hostname: string): Promise<string | null> {
            const { $db } = useNuxtApp();
            const auth = useNuxtApp().$auth;

            const normalizeDomain = (value: string) =>
                value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');

            const findByDomain = async (domain: string, verifiedOnly = true) => {
                const tenantsRef = collection($db, 'tenants');
                const filters = [where('customDomain', '==', domain)];
                if (verifiedOnly) {
                    filters.push(where('domainVerified', '==', true));
                }
                const q = query(tenantsRef, ...filters);
                return getDocs(q);
            };

            try {
                const normalizedHost = normalizeDomain(hostname);
                const domainCandidates = normalizedHost.startsWith('www.')
                    ? [normalizedHost, normalizedHost.slice(4)]
                    : [normalizedHost, `www.${normalizedHost}`];

                // 0) If an internal user is signed in, prefer direct lookups
                // through known tenant IDs. This avoids cross-tenant list-query
                // permission edge cases for member accounts.
                const currentUser = auth.currentUser;
                if (currentUser?.uid) {
                    if (this.tenants.length === 0) {
                        await this.fetchUserTenants(currentUser.uid);
                    }

                    const tenantIds = this.tenants.map(entry => entry.tenantId).filter(Boolean);
                    for (const tenantId of tenantIds) {
                        const tenantRef = doc($db, 'tenants', tenantId);
                        const tenantSnap = await getDoc(tenantRef);
                        if (!tenantSnap.exists()) continue;

                        const tenantData = tenantSnap.data() as Tenant;
                        const tenantDomain = normalizeDomain(String(tenantData.customDomain || ''));
                        if (!tenantDomain) continue;

                        const matchesCandidate = domainCandidates.includes(tenantDomain);
                        if (!matchesCandidate) continue;

                        if (tenantData.domainVerified) {
                            return tenantId;
                        }

                        if (!tenantData.domainVerificationToken) continue;

                        const result = await $fetch<DomainVerificationResult>('/api/verify-domain', {
                            method: 'POST',
                            headers: await getRequiredAuthHeaders(),
                            body: {
                                tenantId,
                                domain: tenantDomain,
                                verificationToken: tenantData.domainVerificationToken,
                            },
                        });

                        if (result.verified) {
                            await updateDoc(tenantRef, {
                                domainVerified: true,
                                updatedAt: new Date(),
                            });
                            return tenantId;
                        }
                    }
                }

                // 1) Fast path: already verified domain match
                for (const candidate of domainCandidates) {
                    try {
                        const verifiedSnap = await findByDomain(candidate, true);
                        if (!verifiedSnap.empty) {
                            const firstVerified = verifiedSnap.docs[0];
                            if (firstVerified) {
                                return firstVerified.id;
                            }
                        }
                    } catch {
                        // Fall through to the next strategy.
                    }
                }

                // 2) Fallback: domain exists but not marked verified yet.
                // Attempt TXT verification on the fly so recently configured
                // domains start working without manual re-save/retry flows.
                for (const candidate of domainCandidates) {
                    let unverifiedSnap;
                    try {
                        unverifiedSnap = await findByDomain(candidate, false);
                    } catch {
                        continue;
                    }
                    if (unverifiedSnap.empty) continue;

                    const tenantDoc = unverifiedSnap.docs[0];
                    if (!tenantDoc) continue;
                    const tenantData = tenantDoc.data() as Tenant;

                    if (!tenantData.domainVerificationToken) continue;

                    const result = await $fetch<DomainVerificationResult>('/api/verify-domain', {
                        method: 'POST',
                        headers: await getRequiredAuthHeaders(),
                        body: {
                            tenantId: tenantDoc.id,
                            domain: candidate,
                            verificationToken: tenantData.domainVerificationToken,
                        },
                    });

                    if (result.verified) {
                        await updateDoc(doc($db, 'tenants', tenantDoc.id), {
                            domainVerified: true,
                            updatedAt: new Date(),
                        });
                        return tenantDoc.id;
                    }
                }

                return null;
            } catch (error) {
                this.error = `Error resolving tenant by domain: ${error}`;
                console.error(this.error);
                return null;
            }
        },

        /**
         * Saves a custom domain for a tenant and generates a DNS TXT
         * verification token.  The tenant owner must then add the token as a
         * DNS TXT record at their domain provider to prove ownership before
         * calling `verifyCustomDomain`.
         *
         * TXT record format:
         *   Host:  _vuey-verify.<domain>
         *   Value: vuey-verify=<token>
         *
         * @param tenantId - ID of the tenant to update.
         * @param domain   - The custom domain to configure (e.g. "app.mybrand.com").
         */
        async setCustomDomain(tenantId: string, domain: string): Promise<void> {
            const { $db } = useNuxtApp();
            const cleanDomain = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
            const verificationToken = crypto.randomUUID().replace(/-/g, '');
            const entitlements = resolveWorkspaceEntitlements(this.currentTenant);

            if (!entitlements.customDomainEnabled) {
                throw new Error('Custom domains are available on Growth with the branding add-on, or on Pro.');
            }

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                await updateDoc(tenantRef, {
                    customDomain: cleanDomain,
                    domainVerified: false,
                    domainVerificationToken: verificationToken,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        customDomain: cleanDomain,
                        domainVerified: false,
                        domainVerificationToken: verificationToken,
                    };
                }
            } catch (error) {
                this.error = `Error setting custom domain: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Verifies domain ownership by triggering a server-side DNS TXT record
         * lookup via `/api/verify-domain`.  On success the tenant document is
         * updated with `domainVerified: true`.
         *
         * @param tenantId - ID of the tenant whose domain should be verified.
         * @returns Verification result including TXT and DNS target checks.
         */
        async verifyCustomDomain(tenantId: string): Promise<DomainVerificationResult> {
            const { $db } = useNuxtApp();

            if (!this.currentTenant?.customDomain || !this.currentTenant?.domainVerificationToken) {
                this.error = 'No custom domain or verification token found';
                return {
                    verified: false,
                    txtVerified: false,
                    dnsTargetVerified: false,
                    expectedTarget: null,
                    routingRecordType: null,
                    vercelIntegrationEnabled: false,
                    vercelProvisionStatus: 'skipped',
                    vercelError: null,
                    vercelVerified: false,
                    vercelVerification: [],
                    vercelRecommendedTarget: null,
                };
            }

            try {
                const result = await $fetch<DomainVerificationResult>('/api/verify-domain', {
                    method: 'POST',
                    headers: await getRequiredAuthHeaders(),
                    body: {
                        tenantId,
                        domain: this.currentTenant.customDomain,
                        verificationToken: this.currentTenant.domainVerificationToken,
                    },
                });

                if (result.verified) {
                    const tenantRef = doc($db, 'tenants', tenantId);
                    await updateDoc(tenantRef, {
                        domainVerified: true,
                        updatedAt: new Date(),
                    });

                    if (this.currentTenant?.id === tenantId) {
                        this.currentTenant = { ...this.currentTenant, domainVerified: true };
                    }
                }

                return result;
            } catch (error) {
                this.error = `Error verifying domain: ${error}`;
                console.error(this.error);
                return {
                    verified: false,
                    txtVerified: false,
                    dnsTargetVerified: false,
                    expectedTarget: null,
                    routingRecordType: null,
                    vercelIntegrationEnabled: false,
                    vercelProvisionStatus: 'skipped',
                    vercelError: null,
                    vercelVerified: false,
                    vercelVerification: [],
                    vercelRecommendedTarget: null,
                };
            }
        },

        /**
         * Removes the custom domain configuration from a tenant.
         *
         * @param tenantId - ID of the tenant to update.
         */
        async removeCustomDomain(tenantId: string): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                await updateDoc(tenantRef, {
                    customDomain: deleteField(),
                    domainVerified: deleteField(),
                    domainVerificationToken: deleteField(),
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    const { customDomain, domainVerified, domainVerificationToken, ...rest } = this.currentTenant;
                    this.currentTenant = rest as Tenant;
                }
            } catch (error) {
                this.error = `Error removing custom domain: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves core workspace identity fields outside of billing/email settings.
         */
        async updateWorkspaceProfile(tenantId: string, profile: Partial<Tenant>): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanProfile = stripUndefined({
                    ...(profile.name !== undefined ? { name: String(profile.name || '').trim() || undefined } : {}),
                    ...(profile.industry !== undefined ? { industry: String(profile.industry || '').trim() || undefined } : {}),
                    ...(profile.companySize !== undefined ? { companySize: String(profile.companySize || '').trim() || undefined } : {}),
                    ...(profile.country !== undefined ? { country: String(profile.country || '').trim() || undefined } : {}),
                    ...(profile.timezone !== undefined ? { timezone: String(profile.timezone || '').trim() || undefined } : {}),
                    ...(profile.primaryGoal !== undefined ? { primaryGoal: String(profile.primaryGoal || '').trim() || undefined } : {}),
                    ...(profile.portalBranding !== undefined ? {
                        portalBranding: stripUndefined({
                            ...(profile.portalBranding || {}),
                            displayFontFamily: String(profile.portalBranding?.displayFontFamily || '').trim() || undefined,
                            bodyFontFamily: String(profile.portalBranding?.bodyFontFamily || '').trim() || undefined,
                            detailHeaderPresentation: profile.portalBranding?.detailHeaderPresentation === 'contained' ? 'contained' : 'immersive',
                        }),
                    } : {}),
                    ...(profile.workspaceAppearance !== undefined ? {
                        workspaceAppearance: stripUndefined({
                            ...(profile.workspaceAppearance || {}),
                            detailHeaderPresentation: profile.workspaceAppearance?.detailHeaderPresentation === 'contained' ? 'contained' : 'immersive',
                        }),
                    } : {}),
                    ...(profile.tenantImageURL !== undefined ? { tenantImageURL: String(profile.tenantImageURL || '').trim() || undefined } : {}),
                    ...(profile.onboardingCompletedAt !== undefined ? { onboardingCompletedAt: profile.onboardingCompletedAt } : {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, cleanProfile);

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        ...cleanProfile,
                    };
                }
            } catch (error) {
                this.error = `Error saving workspace profile: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant payment settings used for invoicing and Stripe metadata.
         */
        async setPaymentSettings(tenantId: string, settings: Tenant['paymentSettings']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    paymentSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        paymentSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving payment settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant email settings used for branded sender identity and white-label delivery.
         */
        async setEmailSettings(tenantId: string, settings: Tenant['emailSettings']): Promise<void> {
            const { $db } = useNuxtApp();
            const entitlements = resolveWorkspaceEntitlements(this.currentTenant);

            if (!entitlements.customEmailDomainEnabled) {
                throw new Error('Custom email sender domains are available on the Pro plan.');
            }

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    emailSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        emailSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving email settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant communication settings used for monitored inbox channels and assistant behavior.
         */
        async setCommunicationSettings(tenantId: string, settings: Tenant['communicationSettings']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    communicationSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        communicationSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving communication settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant authentication settings used for branded workspace sign-in behavior.
         */
        async setAuthSettings(tenantId: string, settings: Tenant['authSettings']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    authSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        authSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving auth settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant task settings used for custom statuses and priorities.
         */
        async setTaskSettings(tenantId: string, settings: Tenant['taskSettings']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    taskSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        taskSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving task settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves tenant project settings used for custom statuses and health states.
         */
        async setProjectSettings(tenantId: string, settings: Tenant['projectSettings']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const cleanSettings = stripUndefined({
                    ...(settings || {}),
                    updatedAt: new Date(),
                });

                await updateDoc(tenantRef, {
                    projectSettings: cleanSettings,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        projectSettings: cleanSettings,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving project settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Saves workspace subscription metadata used for plan entitlements and billing UX.
         */
        async setSubscription(tenantId: string, subscription: Tenant['subscription']): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const tenantRef = doc($db, 'tenants', tenantId);
                const normalized = getTenantSubscription({
                    ...(this.currentTenant || { id: tenantId, name: '', industry: '', tenantImageURL: '', createdAt: new Date(), updatedAt: new Date() }),
                    subscription: {
                        ...subscription,
                        operatorOverride: subscription?.operatorOverride || this.currentTenant?.subscription?.operatorOverride,
                    },
                    tier: subscription?.plan || this.currentTenant?.tier,
                });
                const cleanSubscription = stripUndefined({
                    ...normalized,
                    updatedAt: new Date(),
                }) as Tenant['subscription'];

                await updateDoc(tenantRef, {
                    tier: normalized.plan,
                    subscription: cleanSubscription,
                    updatedAt: new Date(),
                });

                if (this.currentTenant?.id === tenantId) {
                    this.currentTenant = {
                        ...this.currentTenant,
                        tier: normalized.plan,
                        subscription: cleanSubscription,
                        updatedAt: new Date(),
                    };
                }
            } catch (error) {
                this.error = `Error saving subscription settings: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        async ensureStorageCapacity(tenantId: string, additionalBytes: number): Promise<void> {
            const { $db } = useNuxtApp();
            const bytesToAdd = normalizeStorageBytes(additionalBytes);
            if (!bytesToAdd) return;

            let tenant = this.currentTenant?.id === tenantId ? this.currentTenant : null;

            if (!tenant) {
                const tenantRef = doc($db, 'tenants', tenantId);
                const tenantSnap = await getDoc(tenantRef);
                tenant = tenantSnap.exists() ? ({ id: tenantSnap.id, ...tenantSnap.data() } as Tenant) : null;
            }

            const entitlements = resolveWorkspaceEntitlements(tenant);
            const currentUsage = normalizeStorageBytes(tenant?.storageUsageBytes);
            const limitBytes = toStorageLimitBytes(entitlements.storageGb);

            if (currentUsage + bytesToAdd > limitBytes) {
                throw new Error(`This upload would exceed your workspace storage limit. ${formatStorageSize(currentUsage)} of ${formatStorageSize(limitBytes)} is already in use. Upgrade your plan or add more storage to continue.`);
            }
        },

        async adjustStorageUsage(tenantId: string, deltaBytes: number): Promise<void> {
            const { $db } = useNuxtApp();
            if (!Number.isFinite(deltaBytes) || deltaBytes === 0) return;

            try {
                const tenantRef = doc($db, 'tenants', tenantId);

                if (this.currentTenant?.id === tenantId) {
                    const nextUsage = Math.max(0, normalizeStorageBytes(this.currentTenant.storageUsageBytes) + deltaBytes);
                    await updateDoc(tenantRef, {
                        storageUsageBytes: nextUsage,
                        updatedAt: new Date(),
                    });

                    this.currentTenant = {
                        ...this.currentTenant,
                        storageUsageBytes: nextUsage,
                        updatedAt: new Date(),
                    };
                    return;
                }

                await updateDoc(tenantRef, {
                    storageUsageBytes: increment(deltaBytes),
                    updatedAt: new Date(),
                });
            } catch (error) {
                this.error = `Error updating storage usage: ${error}`;
                console.error(this.error);
                throw error;
            }
        },
    },

    getters: {
        /**
         * Get all tenant IDs the user belongs to
         */
        tenantIds: (state) => state.tenants.map(t => t.tenantId),

        /**
         * Get user's role in current tenant
         */
        currentTenantRole: (state) => {
            if (!state.currentTenantId) return null;
            return state.tenants.find(t => t.tenantId === state.currentTenantId)?.role;
        },

        /**
         * Check if user is owner of current tenant
         */
        isCurrentTenantOwner: (state) => 
            state.tenants.find(t => t.tenantId === state.currentTenantId)?.role === 'owner',
    },
});
